import { AInput } from "@/admin/form/Fields.tsx";
import {
    updateDefaultActivePostPaymentLevelMutation,
    updateFirstAccountCreditMutation,
    updateLowBalanceNoticeThresholdMutation,
    updateMinTopupAmountMutation,
    updateRestrictionLevelClearTopupThresholdMutation,
    updateRestrictionLevelThresholdsMutation,
} from "@/admin/settings/settingsQuery.ts";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { controllerProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { RestrictionLevel, RestrictionLevelThresholds } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { produce } from "immer";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface ConfigPaymentModalProps {
    value: number | string;
    type: string;
    title: string;
    level?: RestrictionLevel;
    thresholds?: RestrictionLevelThresholds;
}

interface PaymentMethodButtonProps extends ConfigPaymentModalProps {
    button: ReactNode;
}

export function ConfigPaymentButton(props: PaymentMethodButtonProps) {
    return (
        <WModal button={props.button}>
            <ConfigPaymentModalContent {...props} />
        </WModal>
    );
}

interface ConfigPaymentInputs {
    current: string;
}

function ConfigPaymentModalContent(props: ConfigPaymentModalProps) {
    const { value, type, title, level, thresholds } = props;
    const firstMutation = useStandardMutation(updateFirstAccountCreditMutation);
    const topupMutation = useStandardMutation(updateMinTopupAmountMutation);
    const levelMutation = useStandardMutation(updateRestrictionLevelClearTopupThresholdMutation);
    const defaultMutation = useStandardMutation(updateDefaultActivePostPaymentLevelMutation);
    const restrictionMutation = useStandardMutation(updateRestrictionLevelThresholdsMutation);
    const balanceMutation = useStandardMutation(updateLowBalanceNoticeThresholdMutation);

    const levels = ["CLEAR", "LIMITED"];

    const onSubmit: SubmitHandler<ConfigPaymentInputs> = async (inputs) => {
        const { current } = inputs;

        if (type === "first_account_credit") {
            await firstMutation.mutateAsync({
                body: { first_account_credit: Number(current) },
            });
        } else if (type === "min_topup_amount") {
            await topupMutation.mutateAsync({
                body: { min_topup_amount: Number(current) },
            });
        } else if (type === "restriction_level_clear_topup_threshold") {
            await levelMutation.mutateAsync({
                body: { restriction_level_clear_topup_threshold: Number(current) },
            });
        } else if (type === "default_active_post_payment_level") {
            await defaultMutation.mutateAsync({
                body: { default_active_post_payment_level: current as "CLEAR" | "LIMITED" },
            });
        } else if (type === "restriction_level_thresholds") {
            const body = produce(thresholds ?? [], (body) => {
                const item = body.find((_p) => _p.restriction_level === level);
                if (item) {
                    item.days = Number(current);
                } else {
                    body.push({
                        days: Number(current),
                        restriction_level: notNull(level),
                    });
                }
            });

            await restrictionMutation.mutateAsync({
                body,
            });
        } else if (type === "low_balance_notice_threshold") {
            await balanceMutation.mutateAsync({
                body: current ? { threshold: Number(current) } : {},
            });
        }
    };

    const form = useForm<ConfigPaymentInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            current: value ? String(value) : "",
        },
    });

    const { control } = form;

    return (
        <WModalForm title={`Update ${title.toLowerCase()}`} label="Update" form={form} onSubmit={onSubmit}>
            <Controller
                control={control}
                name="current"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired={type !== "low_balance_notice_threshold"}
                        autoFocus
                        headless
                        block
                        label={title}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        {type === "default_active_post_payment_level" ? (
                            <WSelect
                                valueKey={field.value}
                                getKey={(item) => item}
                                items={levels}
                                aria-label="Select status"
                                getTextValue={(item) => item}
                                onChange={field.onChange}
                            >
                                {(item) => item}
                            </WSelect>
                        ) : (
                            <AInput />
                        )}
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
